import type { PushNotificationSchema } from '@capacitor/push-notifications';

import {
  NotificationAck,
  NotificationAckType,
  NotificationRecord,
  NotificationSource,
  NotificationsRealtimePayload,
} from '../types/Notifications/types';

const NOTIFICATIONS_INBOX_LIMIT = 50;
const NOTIFICATION_TOAST_DEDUP_WINDOW_MS = 3000;
let lastNotificationToast: { signature: string; at: number } | null = null;

export const createNotificationId = (prefix = 'notification'): string =>
  `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

const normalizeString = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';
const normalizeIdentifier = (value: unknown): string => {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return normalizeString(value);
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const resolveNotificationTextFromPayload = (
  payload: Record<string, unknown>,
  keys: string[],
): string => {
  for (const key of keys) {
    const resolvedValue = normalizeString(payload[key]);
    if (resolvedValue) return resolvedValue;
  }

  const nestedPayloadCandidates = [
    payload.notification,
    payload.notificacion,
    payload.data,
    payload.alert,
    payload.aps,
  ];

  for (const candidate of nestedPayloadCandidates) {
    if (!isRecord(candidate)) continue;

    for (const key of keys) {
      const resolvedValue = normalizeString(candidate[key]);
      if (resolvedValue) return resolvedValue;
    }
  }

  return '';
};

const resolveNotificationTitleText = (
  explicitTitle: string,
  payload: Record<string, unknown>,
): string =>
  explicitTitle ||
  resolveNotificationTextFromPayload(payload, [
    'title',
    'titulo',
    'notification_title',
    'notificationTitle',
    'subject',
    'asunto',
  ]);

const resolveNotificationBodyText = (
  explicitBody: string,
  payload: Record<string, unknown>,
): string =>
  explicitBody ||
  resolveNotificationTextFromPayload(payload, [
    'body',
    'message',
    'mensaje',
    'description',
    'descripcion',
    'subtitle',
    'text',
    'notification_body',
    'notificationBody',
  ]);

const resolvePushNotificationIdentifier = (
  payload: Record<string, unknown>,
  nativeNotificationId?: string | null,
): string =>
  normalizeIdentifier(payload.notificationId) ||
  normalizeIdentifier(payload.id) ||
  normalizeIdentifier(payload.notificacionId) ||
  normalizeIdentifier(payload.notification_id) ||
  normalizeIdentifier(payload.notificacion_id) ||
  normalizeIdentifier(nativeNotificationId);

const extractRawNotificationPayload = (
  payload: NotificationsRealtimePayload,
): Record<string, unknown> => {
  const {
    id,
    type,
    title,
    body,
    deepLink,
    createdAt,
    payload: nestedPayload,
    ...rest
  } = payload;

  return {
    ...rest,
    ...(nestedPayload ?? {}),
  };
};

export const resolveBackendNotificationId = (id: string): string | number =>
  /^\d+$/.test(id) ? Number(id) : id;

export const resolveNotificationType = (
  explicitType?: string,
  payload: Record<string, unknown> = {},
): string => {
  if (explicitType) return explicitType;

  const icon = normalizeString(payload.notification_android_icon);
  if (icon.startsWith('icon_')) {
    return icon.replace('icon_', '');
  }

  const tipo = normalizeString(payload.tipo);
  if (tipo) {
    return tipo.toLowerCase();
  }

  if (payload.eventoId == null) {
    return 'usuario';
  }

  return 'general';
};

export const resolveNotificationDeepLink = (
  type: string,
  explicitDeepLink?: string | null,
): string | null => {
  if (explicitDeepLink) return explicitDeepLink;

  switch (type) {
    case 'vacuna':
    case 'despinterna':
    case 'despexterna':
    case 'celo':
    case 'cumpleaños':
    case 'stockalimento':
    case 'general':
      return '/tabs/pet';
    case 'usuario':
      return '/tabs/user';
    default:
      return '/tabs/home';
  }
};

export const buildNotificationRecord = (
  payload: NotificationsRealtimePayload,
  source: NotificationSource,
): NotificationRecord => {
  const now = new Date().toISOString();
  const normalizedPayload = extractRawNotificationPayload(payload);
  const type = resolveNotificationType(payload.type, normalizedPayload);
  const title =
    resolveNotificationTitleText(normalizeString(payload.title), normalizedPayload) ||
    'FielPet';
  const body =
    resolveNotificationBodyText(normalizeString(payload.body), normalizedPayload) ||
    'Tenes una nueva notificacion';

  return {
    id: normalizeIdentifier(payload.id) || createNotificationId(source),
    type,
    title,
    body,
    deepLink: resolveNotificationDeepLink(type, payload.deepLink ?? null),
    createdAt:
      normalizeString(payload.createdAt) ||
      normalizeString(normalizedPayload.createdAt) ||
      now,
    receivedAt: now,
    openedAt: null,
    seenAt: null,
    source,
    payload: normalizedPayload,
  };
};

export const buildNotificationFromPush = (
  notification: PushNotificationSchema,
): NotificationRecord => {
  const payload = (notification.data ?? {}) as Record<string, unknown>;
  const resolvedNotificationId = resolvePushNotificationIdentifier(
    payload,
    notification.id,
  );

  return buildNotificationRecord(
    {
      id: resolvedNotificationId,
      title: notification.title,
      body: notification.body,
      createdAt: normalizeString(payload.createdAt),
      deepLink: normalizeString(payload.deepLink) || null,
      type: normalizeString(payload.type),
      payload: {
        nativeNotificationId: normalizeString(notification.id),
        ...payload,
      },
    },
    'fcm',
  );
};

export const formatNotificationToastMessage = (
  notification: NotificationRecord,
): string =>
  normalizeString(notification.body) ||
  normalizeString(notification.title) ||
  'Tenes una nueva notificacion';

export const shouldDisplayNotificationToast = (
  notification: NotificationRecord,
  dedupWindowMs = NOTIFICATION_TOAST_DEDUP_WINDOW_MS,
): boolean => {
  const signature = [
    normalizeIdentifier(notification.id),
    normalizeString(notification.title),
    normalizeString(notification.body),
  ].join('::');
  const now = Date.now();

  if (
    lastNotificationToast &&
    lastNotificationToast.signature === signature &&
    now - lastNotificationToast.at < dedupWindowMs
  ) {
    return false;
  }

  lastNotificationToast = {
    signature,
    at: now,
  };

  return true;
};

export const upsertNotification = (
  items: NotificationRecord[],
  nextItem: NotificationRecord,
): NotificationRecord[] => {
  const existingIndex = items.findIndex((item) => item.id === nextItem.id);

  if (existingIndex === -1) {
    return [nextItem, ...items].slice(0, NOTIFICATIONS_INBOX_LIMIT);
  }

  const existing = items[existingIndex];
  const merged: NotificationRecord = {
    ...existing,
    ...nextItem,
    openedAt: existing.openedAt ?? nextItem.openedAt,
    seenAt: existing.seenAt ?? nextItem.seenAt,
    receivedAt: existing.receivedAt || nextItem.receivedAt,
  };

  const cloned = [...items];
  cloned.splice(existingIndex, 1);
  return [merged, ...cloned].slice(0, NOTIFICATIONS_INBOX_LIMIT);
};

export const countUnreadNotifications = (items: NotificationRecord[]): number =>
  items.filter((item) => !item.seenAt).length;

export const enqueueNotificationAck = (
  queue: NotificationAck[],
  id: string,
  type: NotificationAckType,
  source: NotificationSource,
): NotificationAck[] => {
  const alreadyQueued = queue.some((ack) => ack.id === id && ack.type === type);
  if (alreadyQueued) return queue;

  return [
    ...queue,
    {
      id,
      type,
      source,
      timestamp: new Date().toISOString(),
    },
  ];
};

export const updateNotificationTimestamp = (
  items: NotificationRecord[],
  id: string,
  key: 'openedAt' | 'seenAt',
): NotificationRecord[] =>
  items.map((item) => {
    if (item.id !== id || item[key]) return item;

    return {
      ...item,
      [key]: new Date().toISOString(),
    };
  });
